import { useState, useContext, useEffect, useMemo, useCallback } from 'react';

import { Layout, Layouts } from 'react-grid-layout';
import debounce from 'lodash/debounce';

import { INTERFACE_PERMISSIONS, UserDashboard, UserDashboards, WidgetItem } from '@vyce/core/src/types';

import { DeviceContext } from '@vyce/core/src/contexts';
import { usePermissions } from '@vyce/core/src/hooks/usePermissions';

import { useActions, useTypedSelector, useInviteButton } from 'src/hooks';

export const usePayrollDashboardData = () => {
  const [payDashboard, setPayDashboard] = useState<UserDashboard>();

  const { dashboards, first_name } = useTypedSelector(state => state.user);
  const { selectedCompany, mainDashboardFilters } = useTypedSelector(state => state.helper);

  const selectedCompanyId = selectedCompany?.uuid ?? '';

  const { updateUserDashboards, updateMainDashboardFilters } = useActions();
  const {
    subscribeLoading,
    isInviteFormOpen,
    isActivateSubscriptionDialogOpen,
    closeActivateSubscriptionDialog,
    handleInviteButtonClick,
    setCloseInviteForm,
    simpleSubscribe,
  } = useInviteButton();
  const { can } = usePermissions();
  const { isMobile } = useContext(DeviceContext);

  const hasTimePermissions = can(INTERFACE_PERMISSIONS.TIME);
  const hasTeamPermissions = can(INTERFACE_PERMISSIONS.TEAMS);

  const onWidgetStatusChange = (systemName: string) => {
    const dashboardWidgets = payDashboard?.widgets || [];
    const newWidgets: WidgetItem[] = dashboardWidgets.map(item => {
      return item.system_name === systemName ? { ...item, is_active: !item.is_active } : item;
    });
    setPayDashboard(value => ({ ...value, widgets: newWidgets }));
  };

  const updateDashboards = useCallback(
    (newWidgets: UserDashboards) => {
      updateUserDashboards(newWidgets);
    },
    [updateUserDashboards]
  );

  const handleUpdateDasboards = useMemo(() => debounce(updateDashboards, 500), [updateDashboards]);

  const resetDashboards = () => {
    if (payDashboard && payDashboard.widgets) {
      const restoredDashboard = payDashboard.widgets.map(item => {
        return { ...item, is_active: true };
      });

      setPayDashboard(value => ({ ...value, widgets: restoredDashboard }));
      updateUserDashboards({
        ...dashboards,
        pay: { ...payDashboard, widgets: restoredDashboard },
      });
    }
  };

  const onLayoutChange = (currentLayout: Layout[], newLayouts: Layouts) => {
    const newWidgetsSettings = { ...payDashboard, layouts: newLayouts };
    setPayDashboard(newWidgetsSettings);
    handleUpdateDasboards({
      ...dashboards,
      pay: newWidgetsSettings,
    });
  };

  const onWidgetsChangeViaSettings = (newWidget: WidgetItem[]) => {
    const newWidgetsSettings = { ...payDashboard, widgets: newWidget };
    setPayDashboard(newWidgetsSettings);
  };

  useEffect(() => {
    if (dashboards?.pay) {
      setPayDashboard(dashboards.pay);
    }
  }, [dashboards?.pay]);

  return {
    isMobile,
    first_name,
    loading: subscribeLoading,
    isActivateSubscriptionDialogOpen,
    payDashboard,
    isInviteFormOpen,
    selectedCompany,
    mainDashboardFilters,
    timePermission: !!hasTimePermissions,
    teamPermission: !!hasTeamPermissions,
    selectedCompanyId,
    onWidgetsChangeViaSettings,
    updateMainDashboardFilters,
    onLayoutChange,
    onWidgetStatusChange,
    closeActivateSubscriptionDialog,
    simpleSubscribe,
    handleInviteButtonClick,
    setCloseInviteForm,
    updateDashboards,
    resetDashboards,
  };
};
